import { AutogramVideoType } from 'types';
import { isAutogramActive, isFullEpisodeWizardActive } from 'utils/routes';
import { VideoTemplateStateContent } from '../../types';
import { getActiveLayers, getLayerIdByAssetId } from '../layers/selectors';

/**
 * This function checks if validation is needed for error-prone scenarios based on the video type and
 * whether certain wizards are active.
 *
 * The validation for error-prone scenarios should occur when the Full Episode wizard is active or when
 * the Autogram wizard is active with a long-form automation (posting full episodes to YouTube or Facebook etc...).
 *
 * @param {AutogramVideoType} videoType - The current video type.
 *
 * @returns A boolean value indicating whether the validation should occur or not.
 */
export function checkShouldValidateTemplate(
  videoType?: AutogramVideoType,
): boolean {
  return (
    isFullEpisodeWizardActive() ||
    (isAutogramActive() && videoType === 'fullEpisode')
  );
}

/**
 * Checks if a given 'videoClip' layer is in the correct position within the list of layers.
 *
 * The 'videoClip' layer is considered to be in the correct position if it is at the beginning or at
 * the end of the list of layers.
 *
 * @param {string} layerId - The ID of the layer to be tested.
 * @param {string[]} layersList - The list of all the layers in the current configuration.
 * @param {number[]} validPositions - array indexes that are valid for 'videoClip' layers.
 * @returns A boolean value indicating whether the 'videoClip' layer is in the correct position or not.
 */
function checkClipIsCorrectPosition(
  layerId: string,
  layersList: string[],
  validPositions: number[],
): boolean {
  const currentLayerIndex = layersList.indexOf(layerId);

  return validPositions.some(position => position === currentLayerIndex);
}

/**
 * Checks if a given video template has a valid configuration for Full Episodes.
 *
 * A template is considered valid for Full Episode if the following conditions are met:
 * If captions are not enabled:
 * - Contains up to two 'videoClips' elements (videos or GIFs).
 * - The 'videoClip' element is at the beginning or at the end (or both in case
 * of multiple elements) of the layers list.
 * if Captions are enabled:
 * - contains at most one 'videoClips' element.
 * - The 'vidoeClip' element is at the end.
 *
 * @param {VideoTemplateStateContent} state - The template state object.
 *
 * @returns A boolean value indicating whether its a valid Full Episode template or not.
 */
export function checkIsValidFullEpisodeTemplate(
  state: VideoTemplateStateContent,
): boolean {
  const {
    videoClips: { order },
    transcription,
  } = state;

  const hasCaptions = transcription?.transcribe;

  const maxVideoClips = hasCaptions ? 1 : 2;

  const validLayerPositions = hasCaptions
    ? [getActiveLayers(state).length - 1]
    : [0, getActiveLayers(state).length - 1];

  if (order.length > maxVideoClips) {
    return false;
  }

  const videoClipsLayersIds = order.map(assetId =>
    getLayerIdByAssetId(state, assetId),
  );

  const isValidClipsConfig = videoClipsLayersIds
    .map(layer =>
      checkClipIsCorrectPosition(
        layer,
        getActiveLayers(state),
        validLayerPositions,
      ),
    )
    .every(Boolean);

  return isValidClipsConfig;
}
